import { Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogDeleteGenericComponent } from '@app/shared/components/dialog-delete-generic/dialog-delete-generic.component';
import { NotificationType } from '@app/shared/interfaces';
import { PropertiesService } from '@app/shared/services/properties/properties.service';
import { RuleService } from '@app/shared/services/rule/rule.service';
import { TeamService } from '@app/shared/services/team/team.service';
import { TitleService } from '@app/shared/services/title/title.service';
import { ToastNotificationsService } from '@app/shared/services/toast-notifications/toast-notifications.service';
import { BehaviorSubject } from 'rxjs';

@Component({
  standalone: false,
  selector: 'sbnb-notification-detail',
  templateUrl: './notification-detail.component.html',
  styleUrls: ['./notification-detail.component.scss'],
})
export class NotificationDetailComponent implements OnInit {
  possibleRoles: any;
  possibleProperties: any;
  displayProperties: BehaviorSubject<any> = new BehaviorSubject([]);
  ruleSetId: any;
  ruleSetData: any;
  loading: boolean;
  saveStatus: number;
  timingsMeta: any;
  isTaskNotification: boolean;
  taskRoles = [
    {
      id: 1,
      name: 'Cleaning',
    },
    {
      id: 2,
      name: 'Check-in',
    },
    {
      id: 4,
      name: 'Check-out',
    },
    {
      id: 8,
      name: 'Maintenance',
    },
  ];

  constructor(
    private teamService: TeamService,
    private location: Location,
    private route: ActivatedRoute,
    private ruleSetService: RuleService,
    private dialog: MatDialog,
    private router: Router,
    private titleService: TitleService,
    private propertiesService: PropertiesService,
    private toast: ToastNotificationsService
  ) { }

  ngOnInit() {
    this.teamService.getPossibleRoles().subscribe((res) => {
      this.possibleRoles = res;
    });

    this.propertiesService.getScopingProperties([], null, null, 5000, false).subscribe((res) => {
      this.possibleProperties = res.data;
      this.displayProperties.next(this.possibleProperties);
    });

    this.titleService.manuallySet('Notification rule');
    this.loading = true;

    this.route.params.subscribe((params) => {
      this.ruleSetId = params['uuid'];
      this.ruleSetService.getRuleSetById(this.ruleSetId, true).subscribe((res) => {
        if (res.error) {
          this.router.navigate(['/operations/notifications']);
          return;
        }

        this.ruleSetData = res.data;
        this.isTaskNotification = this.ruleSetData.event.startsWith('team:task:');
        this.timingsMeta = res.meta?.timings;
        this.titleService.manuallySet(this.ruleSetData.name);
        this.loading = false;
      });
    });
  }

  timingUpdated($event) {
    this.save();
  }

  save() {
    this.saveStatus = 1;
    this.ruleSetService.updateRuleSet(this.ruleSetId, this.ruleSetData).subscribe((res: any) => {
      if (res.error) {
        this.saveStatus = -1;
        return;
      }

      this.saveStatus = 2;
      setTimeout(() => {
        this.saveStatus = null;
      }, 2000);
    });
  }

  ruleSaveStatusChange($event) {
    this.saveStatus = $event;

    if ($event === 2) {
      setTimeout(() => {
        this.saveStatus = null;
      }, 2000);
    }
  }

  allPropertyChange(event: MatCheckboxChange) {
    this.ruleSetData.all_properties = event.checked;
    this.save();
  }

  copyRuleset() {
    this.ruleSetService.copyRuleset(this.ruleSetId).subscribe((res) => {
      if (res.data.id) {
        this.router.navigate(['/operations/notifications/detail/', res.data.id]);
      }
    });
  }

  propertyCheckboxChange(property, event: MatCheckboxChange) {
    if (event.checked) {
      if (this.ruleSetData.properties.indexOf(property.id) === -1) {
        this.ruleSetData.properties.push(property.id);
        this.save();
      }
    } else {
      const index = this.ruleSetData.properties.findIndex((r) => r === property.id);

      if (index !== -1) {
        this.ruleSetData.properties.splice(index, 1);
        this.save();
      }
    }
  }

  handlesettingsChange(event) {
    this.save();
  }

  roleChange(role, event) {
    if (event.checked) {
      this.ruleSetData.team.services.push(role.id);
    } else {
      const index = this.ruleSetData.team.services.findIndex((x) => x === role.id);
      if (index !== -1) {
        this.ruleSetData.team.services.splice(index, 1);
      }
    }

    this.save();
  }

  goBack() {
    this.router.navigate(['/operations/notifications']);
  }

  deleteRulesetPrompt() {
    const dialogRef = this.dialog.open(DialogDeleteGenericComponent, {
      width: '720px',
      data: {
        name: 'Notification rule',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleteRuleset();
      }
    });
  }

  deleteRuleset() {
    this.ruleSetService.deleteRuleset(this.ruleSetId).subscribe((res) => {
      this.location.back();
    });
  }

  activateOrDeactivateRule(event: MatCheckboxChange) {
    this.ruleSetData.active = event.checked;

    this.ruleSetService.activeOrDeactivateRuleset(this.ruleSetId, this.ruleSetData.active).subscribe((res: any) => {
      if (res.success) {
        this.saveStatus = 2;
        setTimeout(() => {
          this.saveStatus = null;
        }, 2500);
        return;
      }

      if (res.error) {
        this.ruleSetData.active = !this.ruleSetData.active;
        this.toast.open(`Oops! We ran into an error updating the Notification rule`, 'Dismiss', NotificationType.Error);
      }
    });
  }

  propertyTrackby(index: number, property: any) {
    return property.id;
  }

  propertySearchChanged(searchCriteria: string) {
    this.displayProperties.next(
      this.possibleProperties.filter((prop) => prop.name.toLowerCase().includes(searchCriteria.toLowerCase()))
    );
  }
}
